import React, { useState, useEffect, useCallback, memo } from 'react';
import {
  Box,
  CircularProgress,
  Typography,
} from '@mui/material';
import { SimpleTreeView, TreeItem } from '@mui/x-tree-view';
import {
  Folder as FolderIcon,
  FolderShared as FolderSharedIcon
} from '@mui/icons-material';
import api from '../../services/api';

// Simple tree item label component
const TreeItemLabel = memo(({ icon, label, isLoading }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
    {isLoading ? <CircularProgress size={16} /> : icon}
    <Typography variant="body2">{label}</Typography>
  </Box>
));

TreeItemLabel.displayName = 'TreeItemLabel';

// Main FolderTree component
// eslint-disable-next-line max-lines-per-function
export default function FolderTree({ driveId, currentFolderId, onFolderSelect, cache }) {
  const [expandedNodes, setExpandedNodes] = useState(new Set([driveId]));
  const [loadingNodes, setLoadingNodes] = useState(new Set());
  const [folderChildren, setFolderChildren] = useState(new Map());
  const [driveInfo, setDriveInfo] = useState(null);
  const [error, setError] = useState(null);
  const [selectedNodeId, setSelectedNodeId] = useState(currentFolderId);

  // Load folder children with caching
  const loadFolderChildren = useCallback(async (folderId) => {
    // Check if already loaded using functional state update
    let alreadyLoaded = false;
    let existingChildren = null;
    setFolderChildren(prev => {
      alreadyLoaded = prev.has(folderId);
      if (alreadyLoaded) {
        existingChildren = prev.get(folderId);
      }
      return prev;
    });

    if (alreadyLoaded) {
      return existingChildren;
    }

    // Check cache first
    let cachedData = cache.get(driveId, folderId, { pageSize: 1000 });

    if (!cachedData) {
      setLoadingNodes(prev => new Set([...prev, folderId]));

      try {
        const result = await api.getFolderContents(driveId, folderId, {
          pageSize: 1000 // Get all folders for tree view
        });

        cache.set(driveId, folderId, result, { pageSize: 1000 });
        cachedData = result;
      } catch (err) {
        console.error('Error loading folder children:', err);
        setError(err.message);
        return [];
      } finally {
        setLoadingNodes(prev => {
          const next = new Set(prev);
          next.delete(folderId);
          return next;
        });
      }
    }

    // Only return folders for tree view
    const folders = cachedData.folders || [];

    setFolderChildren(prev => new Map([...prev, [folderId, folders]]));
    return folders;
  }, [driveId, cache]);

  // Load drive info on mount
  useEffect(() => {
    const loadDriveInfo = async () => {
      try {
        const drive = await api.getSharedDriveDetails(driveId);
        setDriveInfo(drive);

        // Load root level folders
        await loadFolderChildren(driveId);
      } catch (err) {
        console.error('Error loading drive info:', err);
        setError(err.message);
      }
    };

    if (driveId) {
      loadDriveInfo();
    }
  }, [driveId, loadFolderChildren]);

  // Update selected node when currentFolderId changes
  useEffect(() => {
    setSelectedNodeId(currentFolderId);
  }, [currentFolderId]);

  // Expand path to current folder
  useEffect(() => {
    const expandPathToFolder = async () => {
      if (!currentFolderId || currentFolderId === driveId) {
        return;
      }

      try {
        // Get the path from API
        const pathResult = await api.getFolderPath(driveId, currentFolderId);
        if (pathResult?.path && pathResult.path.length > 0) {
          const pathIds = pathResult.path.map(p => p.id);

          // Load children for all nodes in path (in order)
          for (const pathItem of pathResult.path) {
            // eslint-disable-next-line max-depth
            if (!folderChildren.has(pathItem.id)) {
              await loadFolderChildren(pathItem.id);
            }
          }

          // Also load children for the current folder if not already loaded
          if (!folderChildren.has(currentFolderId)) {
            await loadFolderChildren(currentFolderId);
          }

          // After loading, expand all nodes in the path including the current folder
          setExpandedNodes(prev => {
            const next = new Set(prev);
            pathIds.forEach(id => next.add(id));
            // Also add the current folder to ensure it's expanded
            next.add(currentFolderId);
            return next;
          });
        }
      } catch (err) {
        console.error('Error expanding path to folder:', err);
        // If path API fails, at least try to expand the parent if we know it
        setExpandedNodes(prev => {
          const next = new Set(prev);
          next.add(driveId);
          return next;
        });
      }
    };

    if (currentFolderId) {
      expandPathToFolder();
    }
  }, [currentFolderId, driveId, loadFolderChildren, folderChildren]);

  // Handle tree expansion
  const handleToggle = useCallback((event, nodeIds) => {
    setExpandedNodes(new Set(nodeIds));

    // Load children for newly expanded nodes
    nodeIds.forEach(nodeId => {
      if (!folderChildren.has(nodeId) && !loadingNodes.has(nodeId)) {
        loadFolderChildren(nodeId);
      }
    });
  }, [folderChildren, loadFolderChildren, loadingNodes]);

  // Handle tree selection
  const handleSelect = useCallback((event, nodeId) => {
    if (nodeId) {
      onFolderSelect(nodeId);
    }
  }, [onFolderSelect]);

  // Recursively render folder tree with circular reference protection
  const renderFolderNode = useCallback((folder, level = 0, visitedNodes = new Set()) => {
    // Max depth protection (10 levels)
    if (level > 10) {
      console.warn(`Max depth reached (10 levels) for folder: ${folder.name}`);
      return null;
    }

    // Prevent rendering the same node twice (circular reference protection)
    if (visitedNodes.has(folder.id)) {
      console.warn(`Circular reference detected for folder: ${folder.id} - ${folder.name}`);
      return null;
    }

    // Create a new Set for this branch to track visited nodes
    const branchVisited = new Set(visitedNodes);
    branchVisited.add(folder.id);

    const isLoading = loadingNodes.has(folder.id);
    const children = folderChildren.get(folder.id) || [];

    const hasLoadedChildren = folderChildren.has(folder.id);

    return (
      <TreeItem
        key={folder.id}
        itemId={folder.id}
        label={
          <TreeItemLabel
            icon={<FolderIcon fontSize="small" color="action" />}
            label={folder.name}
            isLoading={isLoading}
          />
        }
        sx={{
          // Force indentation at component level
          marginLeft: level > 0 ? `${level * 20}px` : 0,
          '& .MuiTreeItem-content': {
            paddingLeft: '0px !important'
          }
        }}
      >
        {/* Always render children if they exist - let TreeView handle expand/collapse */}
        {hasLoadedChildren && children.length > 0 &&
          children.map(child => renderFolderNode(child, level + 1, branchVisited))
        }
        {/* Show invisible placeholder to trigger expand icon for unloaded folders */}
        {!hasLoadedChildren && <div style={{ display: 'none' }}>placeholder</div>}
      </TreeItem>
    );
  }, [loadingNodes, folderChildren]);

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error" variant="body2">
          Error loading folder tree: {error}
        </Typography>
      </Box>
    );
  }

  if (!driveInfo) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  const rootChildren = folderChildren.get(driveId) || [];

  return (
    <Box sx={{ p: 1 }}>
      <SimpleTreeView
        defaultExpandedItems={[driveId]}
        expandedItems={Array.from(expandedNodes)}
        selectedItems={selectedNodeId}
        onExpandedItemsChange={handleToggle}
        onSelectedItemsChange={handleSelect}
        sx={{
          '& .MuiTreeItem-content': {
            padding: '4px 0',
            margin: 0,
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)'
            },
            '&.Mui-expanded': {
              fontWeight: 'normal'
            },
            '&.Mui-selected': {
              backgroundColor: 'rgba(25, 118, 210, 0.08)',
              '&.Mui-focused': {
                backgroundColor: 'rgba(25, 118, 210, 0.12)'
              },
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.16)'
              }
            }
          },
          '& .MuiTreeItem-label': {
            fontSize: '0.875rem',
            padding: 0
          },
          // Force indentation for nested items
          '& .MuiTreeItem-group': {
            marginLeft: '28px !important',
            paddingLeft: '8px',
            borderLeft: '1px solid rgba(0, 0, 0, 0.12)',
            '& .MuiTreeItem-content': {
              paddingLeft: '0px'
            }
          }
        }}
      >
        {/* Drive root */}
        <TreeItem
          itemId={driveId}
          label={
            <TreeItemLabel
              icon={<FolderSharedIcon fontSize="small" color="primary" />}
              label={driveInfo.name}
              isLoading={loadingNodes.has(driveId)}
            />
          }
        >
          {rootChildren.map(folder => renderFolderNode(folder, 1, new Set([driveId])))}
        </TreeItem>
      </SimpleTreeView>
    </Box>
  );
}